import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** One line under the title (whose it is, its state). */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Actions pinned at the bottom of the panel («Hecho», «Guardar»…), or a
   *  composer that must stay in view (a reply box). */
  footer?: React.ReactNode;
  /** Buttons in the header, left of «✕» (an «Abrir en…», an `OverflowMenu`). */
  actions?: React.ReactNode;
  /** Panel width from `sm` up (full width on a phone). Default `sm:max-w-md`. */
  width?: string;
}

/**
 * Drawer — a side panel that opens over the right edge of the page
 * (Standards §20.13), for the detail or the edit of ONE row of a list without
 * leaving the list: the list stays visible (dimmed) behind it.
 *
 * Use it where a centred modal hid the list you were working through (a
 * support case, an alarm type, a country's profile). It is a dialog: portalled
 * to `<body>`, `role="dialog"` + `aria-modal`, Escape and a click on the dimmed
 * page close it, focus moves in on open (trapped with Tab) and back on close.
 *
 * @example
 * <Drawer isOpen={!!kind} onClose={() => setKind(null)} title={kind?.label} subtitle={kind?.kind}
 *         footer={<Button onClick={() => setKind(null)}>Hecho</Button>}>
 *   …
 * </Drawer>
 */
export default function Drawer({ isOpen, onClose, title, subtitle, children, footer, actions, width = 'sm:max-w-md' }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // A dialog opened over the panel (a ConfirmDialog, a menu) closes
        // first: its Escape is not the panel's.
        const active = document.activeElement;
        if (active && panelRef.current && !panelRef.current.contains(active) && active.closest('[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"]')) return;
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const items = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const raf = requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus());
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
      previous?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => onCloseRef.current()}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`h-full w-full ${width} flex flex-col bg-white dark:bg-surface-dark border-l border-border-subtle dark:border-border-subtle-dark shadow-2xl slide-in-from-right`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-subtle dark:border-border-subtle-dark shrink-0">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-text-main dark:text-white break-words">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-text-sub dark:text-slate-400 break-words">{subtitle}</p> : null}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {actions}
            <Button type="button" variant="ghost" size="sm" icon="close" iconOnly aria-label="Cerrar" title="Cerrar" onClick={() => onCloseRef.current()} />
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="shrink-0 px-5 py-3 border-t border-border-subtle dark:border-border-subtle-dark flex justify-end gap-2">{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
